import { createSelector } from 'reselect';
import { validators } from '../validators';
import { ChildFields } from '../models/child';
import { compareChild } from '../helpers/compareChild';
import { isAdult, shouldBeGettingEducation } from '../helpers/dateHelpers';
import { getValue } from '../constants/getFieldValues';
import {
  isCitizen,
  isRussianPassport,
  getPassportStatus,
} from '../validators/common';

export const getChildren = (state): ChildFields[] =>
  Object.values(state.children);

export const getChildrenObject = (state): Record<string, any> => state.children;

export const getNotHiddenChildrenIds = (state): (string | number)[] =>
  state.notHiddenChildren;

export const getNotHiddenChildren = createSelector(
  getChildrenObject,
  getNotHiddenChildrenIds,
  (children, notHiddenChildrenIds): ChildFields[] =>
    notHiddenChildrenIds.map((id) => children[id])
);

export const getHiddenChildren = createSelector(
  getChildrenObject,
  getNotHiddenChildrenIds,
  (children, notHiddenChildrenIds) =>
    Object.keys(children)
      .filter((id) => !notHiddenChildrenIds.includes(id))
      .map((id) => children[id])
      .sort(compareChild)
);

export const childrenStepCompleted = createSelector(
  (state) => validators.childrenStepCompleted(state),
  (isCompleted) => isCompleted
);

export const atLeastOneKidLessThan16 = createSelector(
  getNotHiddenChildren,
  (children: ChildFields[]): boolean =>
    children.some(
      ({ birthdate: { value } }): boolean =>
        !shouldBeGettingEducation(value) && !isAdult(value)
    )
);

export const atLeastOneKidOlder16IsStudying = createSelector(
  getNotHiddenChildren,
  (children: ChildFields[]): boolean =>
    children.some(
      ({ birthdate: { value }, isStudying }): boolean =>
        shouldBeGettingEducation(value) && !isAdult(value) && !!isStudying
    )
);

type ChildrenPassportStatuses = {
  id: string;
  status: string;
};

export const validateChildrenPassportStatus = createSelector(
  getNotHiddenChildren,
  (children: ChildFields[]): ChildrenPassportStatuses[] => {
    return children.map((child) => {
      const birthday = getValue(child.birthdate);
      const issueDate = getValue(child.dul.issueDate);
      const isPassportRf =
        isCitizen(getValue(child.citizenship)) &&
        isRussianPassport(child.dul.type.value);
      const status = getPassportStatus(birthday, issueDate, isPassportRf);
      return { id: child.id, status };
    });
  }
);
